import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SCHEDULED_REPORT_TYPES,
  SCHEDULED_REPORT_TYPE_LABELS,
  SCHEDULE_FREQUENCIES,
  SCHEDULE_FREQUENCY_LABELS,
  SCHEDULE_FORMATS,
  type ScheduledReportType,
  type ScheduleFrequency,
  type ScheduleFormat,
} from '@cls/shared';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Contact { id: string; name: string; email: string | null; role: string | null; customer: string | null; }

interface Initial {
  id?: string;
  reportType?: ScheduledReportType;
  schedule?: ScheduleFrequency;
  weekday?: number | null;
  dayOfMonth?: number | null;
  time?: string;
  format?: ScheduleFormat;
  recipients?: string[];
  enabled?: boolean;
}

interface Props { onClose: () => void; initial?: Initial; }

const REPORT_TYPE_ICONS: Record<ScheduledReportType, string> = {
  'compliance': '📊',
  'compliance-expired': '✗',
  'compliance-expiring': '⚠',
  'compliance-missing': '✗',
  'compliance-suppliers': '🛠',
  'compliance-docs': '✍',
  'compliance-geofence': '📍',
  'compliance-dataquality': '🔍',
  'deployments': '🚀',
  'projects-keyroles': '👥',
  'timesheet': '⏱',
  'calendar': '🗓',
  'training-matrix': '◫',
  'training-matrix-deployed-by-project': '🏗',
  'training-matrix-deployed-flat': '◫',
  'visa-expiry': '🛂',
  'deployment-week': '📅',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ScheduledReportModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;

  const [reportType, setReportType] = useState<ScheduledReportType>(
    (initial?.reportType as ScheduledReportType) ?? 'compliance',
  );
  const [frequency, setFrequency] = useState<ScheduleFrequency>(
    (initial?.schedule as ScheduleFrequency) ?? 'weekly',
  );
  const [dayOfWeek, setDayOfWeek] = useState<number>(initial?.weekday ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState<number>(initial?.dayOfMonth ?? 1);
  const [time, setTime] = useState(initial?.time ?? '09:00');
  const [format, setFormat] = useState<ScheduleFormat>((initial?.format as ScheduleFormat) ?? 'pdf');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selected, setSelected] = useState<string[]>(initial?.recipients ?? []);
  const [extraEmail, setExtraEmail] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data),
  });

  const filtered = useMemo(() => {
    const s = recipientSearch.toLowerCase().trim();
    return contacts.filter((c) => c.email && (!s || c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s)));
  }, [contacts, recipientSearch]);

  const toggle = (email: string) => setSelected((p) => p.includes(email) ? p.filter((x) => x !== email) : [...p, email]);
  const toggleAll = (on: boolean) => setSelected(on ? filtered.map((c) => c.email!).filter(Boolean) : []);
  const addExtra = () => {
    const v = extraEmail.trim();
    if (v && /@/.test(v) && !selected.includes(v)) {
      setSelected([...selected, v]);
      setExtraEmail('');
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        reportType,
        schedule: frequency,
        time,
        format,
        recipients: selected,
        enabled: initial?.enabled ?? true,
      };
      if (frequency === 'weekly') body.weekday = dayOfWeek;
      if (frequency === 'monthly' || frequency === 'quarterly') body.dayOfMonth = dayOfMonth;
      if (editing && initial?.id) return api.patch(`/scheduled-reports/${initial.id}`, body);
      return api.post('/scheduled-reports', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success(editing ? 'Schedule updated' : 'Schedule created', SCHEDULED_REPORT_TYPE_LABELS[reportType]);
      onClose();
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error('Save failed', e?.response?.data?.message ?? 'Check fields'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 640, maxWidth: '98vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{editing ? '✎ Edit Scheduled Report' : '📨 New Scheduled Report'}</div>
        <div className="modal-sub">Configure automated report delivery</div>

        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Report Type</label>
            <select className="form-select" value={reportType} onChange={(e) => setReportType(e.target.value as ScheduledReportType)}>
              {SCHEDULED_REPORT_TYPES.map((t) => (
                <option key={t} value={t}>{`${REPORT_TYPE_ICONS[t]} ${SCHEDULED_REPORT_TYPE_LABELS[t]}`}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Frequency</label>
            <select className="form-select" value={frequency} onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}>
              {SCHEDULE_FREQUENCIES.map((f) => (
                <option key={f} value={f}>{SCHEDULE_FREQUENCY_LABELS[f]}</option>
              ))}
            </select>
          </div>
        </div>

        {frequency === 'weekly' ? (
          <div className="form-row">
            <label className="form-label">Day of Week</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {DAY_LABELS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDayOfWeek(i)}
                  style={{ flex: 1, padding: '6px 4px', fontSize: 11, border: `1px solid ${dayOfWeek === i ? 'var(--accent)' : 'var(--border)'}`, background: dayOfWeek === i ? 'rgba(232,164,39,.2)' : 'var(--card)', color: dayOfWeek === i ? 'var(--accent)' : 'var(--muted)', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {frequency === 'monthly' || frequency === 'quarterly' ? (
          <div className="form-row">
            <label className="form-label">Day of Month</label>
            <select className="form-select" value={dayOfMonth} onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10))} style={{ width: 120 }}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Time</label>
            <input type="time" className="form-input" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Export Format</label>
            <select className="form-select" value={format} onChange={(e) => setFormat(e.target.value as ScheduleFormat)}>
              {SCHEDULE_FORMATS.map((f) => (
                <option key={f} value={f}>{f === 'pdf' ? 'PDF' : 'Excel / CSV'}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Recipients <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 10 }}>({selected.length} selected)</span></span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => toggleAll(true)}>Select all</button>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => toggleAll(false)}>Clear</button>
            </span>
          </label>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 9px', gap: 6, marginBottom: 8 }}>
              <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
              <input type="text" placeholder="Search contacts by name or email…" value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 12, width: '100%' }} />
            </div>
            <div style={{ maxHeight: 180, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {filtered.map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', padding: 4, borderRadius: 4, background: selected.includes(c.email!) ? 'rgba(232,164,39,.15)' : 'var(--surface)' }}>
                  <input type="checkbox" checked={selected.includes(c.email!)} onChange={() => toggle(c.email!)} style={{ accentColor: 'var(--accent)' }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 9 }}>{c.email?.split('@')[0]}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <input type="email" placeholder="Additional email address…" value={extraEmail} onChange={(e) => setExtraEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtra(); } }} className="form-input" style={{ flex: 1 }} />
              <button className="btn btn-ghost btn-xs" onClick={addExtra} disabled={!extraEmail.trim()}>+ Add</button>
            </div>
            {selected.length > 0 ? (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.map((e) => (
                  <span key={e} className="pill" style={{ fontSize: 9 }}>
                    {e} <button onClick={() => toggle(e)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 0, marginLeft: 4 }}>✕</button>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 6, fontSize: 10, color: 'var(--muted)' }}>Tick contacts to include as recipients. Only contacts with email addresses are shown.</div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || selected.length === 0}>
            {save.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Schedule'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
